import React, { useCallback } from 'react';
import {
  Link, useHistory, useLocation, useParams,
} from 'react-router-dom';
import VoucherButton from 'features/voucher/VoucherButton';
import { getVoucherBatchPath } from 'shared/routes/voucher';
import { getVoucherReport } from 'shared/api/vouchers';
import { FilterRedirect } from 'shared/hoc/withFilters';
import DataTable from 'shared/components/table/DataTable';
import { wrapList } from 'shared/utils';
import { FILTER_DISPLAY_TYPE, VOUCHER_TYPES } from 'shared/constants';
import { withStaticSelect } from 'shared/components/data-display/withSelectors';
import dateFilters from './helpers';

const options = Object.values([
  { ...VOUCHER_TYPES.free },
  { ...VOUCHER_TYPES.billable },
])
  .map(({ id, label }) => ({ label, value: id }));

const redeemedReportFilters = [
  ...dateFilters,
  {
    label: 'Voucher type',
    param: 'voucher_type',
    type: FILTER_DISPLAY_TYPE.control,
    FilterComponent: withStaticSelect({
      options,
    }),
  },
];

/**
 * @param {Object} props
 * @param {string} props.model - One of: '', 'property', 'complex', 'manager'
 * @param {string} props.filterKey
 * @param {string} props.savedFilters
 * @param {string} props.path
 */
// A report that shows individual redeemed vouchers and their costs.
export default function RedeemedReport({
  model, filterKey, savedFilters, path,
}) {
  const { search } = useLocation();
  const history = useHistory();
  const { routeId } = useParams();
  const activeVoucher = new URLSearchParams(search).get('voucher');

  const setActiveVoucher = (newVoucher) => {
    const searchParams = new URLSearchParams(search);
    if (!newVoucher) {
      searchParams.delete('voucher');
    } else {
      searchParams.set('voucher', newVoucher);
    }
    history.push(`?${searchParams.toString()}`);
  };

  const columns = [
    {
      name: 'Voucher',
      formatter: (row, _, i, rows) => (
        <VoucherButton
          active={activeVoucher === row.voucher}
          changeActiveVoucher={(n) => setActiveVoucher(wrapList(rows, i, n).voucher)}
          container="row"
          key={row.voucher}
          setActiveVoucher={setActiveVoucher}
          voucher={row}
        />
      ),
    },
    {
      name: 'Batch #',
      formatter: (row) => <Link to={getVoucherBatchPath(row.batchId)}>{row.batchId}</Link>,
      orderBy: 'batch_id',
    },
    {
      name: 'Service',
      jsonKey: 'productName',
      orderBy: 'iiq_speed',
    },
    {
      name: 'Service Days',
      jsonKey: 'serviceDays',
      orderBy: '18',
    },
    {
      name: 'Daily Cost',
      jsonKey: 'dailyCost',
      orderBy: 'daily_cost',
    },
    {
      name: 'Bill',
      jsonKey: 'bill',
      orderBy: '20',
    },
  ];

  const searchRequest = useCallback(
    (searchStr, accept) => getVoucherReport(searchStr, accept, model, routeId),
    [model, routeId],
  );

  const result = FilterRedirect(path, savedFilters);
  if (result) {
    return result;
  }

  return (
    <DataTable
      tableName="Redeemed Vouchers"
      searchRequest={searchRequest}
      ignoredKeys={['voucher']}
      rowId="voucher"
      columns={columns}
      filters={redeemedReportFilters}
      filterKey={filterKey}
      className="table-height-large"
    />
  );
}
